/**
 * The day, programmed — the whole form, in every host that draws one.
 *
 * ── One component, three hosts, and NOTHING in here differs between them ─────────────────────
 * The builder's 574px panel, the builder's day modal and the calendar's day editor render the same
 * list, the same head and the same library drawer. This file branched on a `dock` prop until 13
 * September 2026, when the old fullscreen host — its own two-row head, a 460px library COLUMN
 * beside the list, and a load-contribution column in each card — was replaced by the owner's modal,
 * which is the panel's own chrome in a bigger box. The branch went with it, and so did the `rail`
 * slot and the `breadcrumb` only that head printed.
 *
 * What the hosts differ in is now entirely outside this file: the box (`DayPanelHost`, which draws
 * both of the builder's, or one of the calendar's docks), whether a Save is handed over, and whether
 * there is another host to switch to.
 *
 * ── The heading hierarchy is the dock's own ──────────────────────────────────────────────────
 * `h2` names the dock and carries the workout, `h4` is each exercise, and each set table is
 * labelled by its `h4`. The page's `h1` is the plan. A reader navigating by heading gets
 * plan → workout → exercise with nothing skipped.
 *
 * ── The two ways into the library are NAMED pills that are also drop targets ────────────────
 * One per block: "+ Ćwiczenie" floating at the foot of the list, "+ Rozgrzewka" under the warm-up
 * run. They were dashed zones carrying a sentence about the drag from 10 August 2026 until the
 * 07.09 mock reversed it; `DropZoneView` in `model/` carries the reasoning and what the reversal did
 * not take away. Both still receive a library row, and both still open the library on a press.
 *
 * ── The BUILDER's panel has no Save of its own any more, and that has a cost ────────────────
 * Owner decision, 7 September 2026: the mock saves from the ember button in the screen's top bar and
 * nowhere else, so the builder hands `save={null}` and the foot bar is not drawn. What that costs
 * depends on the box, and it was measured on 13 September 2026 rather than assumed: the builder's
 * panel is `modal={false}`, nothing behind it carries `aria-hidden`, and the bar's Zapisz is
 * reachable by pointer and by keyboard. The MODAL is modal — the screen's `<header>` carries
 * `aria-hidden="true"` and the button resolves by role 0 times — so there Cmd/Ctrl+S is the only
 * save, which is why `DayPanelHead` prints the shortcut on the heading's description whenever `save`
 * is null. (An earlier note here said the panel hid the document too. It does not.)
 *
 * The bar itself is NOT deleted: the calendar's day editor is this same component in a dock with no
 * bar above it, so `save` stays the switch it always was and that host still passes one.
 *
 * ── Every value in it is real ────────────────────────────────────────────────────────────────
 * The session note is `SessionTemplate.Notes`, snapshotted onto the client's workout. The exercise
 * note is the coaching tip the athlete reads in the logger. The day's rest is a SEED: changing it
 * restamps every exercise that was following it and leaves alone every one that was not. The
 * warm-up rows are real prescriptions — a `Warmup` role exercise has always carried sets, and this
 * gallery is the first canon to draw them. The only mock on this surface is the per-set note (gap
 * G4), and it is the one field that says nothing about surviving a save, because it does not.
 */

import { useId, useRef, type ReactNode, type RefObject } from 'react';

import {
  groupAsSuperset,
  previewMoveCommand,
  setDayRest,
  setSessionNotes,
} from '~/entities/plan-draft';
import { useAutoScroll } from '~/shared/ui/dnd/useAutoScroll';
import { useDropTarget } from '~/shared/ui/dnd/useDropTarget';
// The visually-hidden rule, borrowed rather than copied — the same borrow
// `AssignedPlanCalendarScreen` makes. `LiveRegionAnnouncer` owns the one declaration of it in
// `shared/ui`, and a fifth transcription of `clip-path: inset(50%)` in this repo would be four too
// many.
import srOnly from '~/shared/ui/LiveRegionAnnouncer.module.css';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { CalendarMarkIcon, DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { useScreenMotionPreference } from '~/shared/design-system/motion';

import type { AuthorableExerciseRole } from '~/entities/plan-draft';

import type { ExerciseView, PrepBoxView } from '../model/dayProgramming';
import {
  collapseAllLabel,
  dayPanelCaption,
  dayStepperWords,
  supersetJoinedMessage,
  supersetModeView,
} from '../model/dayProgramming';
import { useDayPanelChrome } from '../lib/useDayPanelChrome';
import { useDayStepMotion } from '../lib/useDayStepMotion';
import { useDepartures } from '../lib/useDepartures';
import { CARD_OUT_MS } from '../model/exitTiming';
import { useDayProgramming } from '../lib/useDayProgramming';
import { useExerciseMove } from '../lib/useExerciseMove';
import { parseRestCell } from '../model/setCellValue';
import {
  exerciseLiftEntry,
  exerciseLiftLabel,
  exerciseLiftedWords,
  exerciseMoveMenu,
  exercisePositionWords,
  type DayMoveTarget,
} from '../model/exerciseMove';

import {
  DayMissingHead,
  DayPanelFoot,
  DayPanelHead,
  DaySupersetBar,
  type DayFormSave,
  type DayHostSwitch,
} from './DayChrome';
import { ExerciseBlock } from './ExerciseBlock';
import { MoveableBlock, type BlockDragSource } from './MoveableBlock';
import { AI_WRITTEN_NOTE_ID } from './aiWrittenNote';
import styles from './DayForm.module.css';

export type { DayFormSave, DayHostSwitch } from './DayChrome';

const KEY = 'web:planBuilder.day';

/**
 * Everything `features/plan-progression` draws inside a day, as three named entry points.
 *
 * One prop rather than three, because they are one composition: the page builds them all from the
 * same slice, and a component that took three render props would make every host wire three things
 * that always travel together. The feature owns the state behind them — which exercise's rule sheet
 * is open, and which matrices are expanded — so nothing about that leaks into this form.
 */
export interface DayProgression {
  /** The "System progresji" group in the card's side column. */
  readonly renderPanel: (exerciseSlotToken: string) => ReactNode;
  /** The cycle matrix under the set table, when the trainer has expanded it. */
  readonly renderMatrix: (exerciseSlotToken: string) => ReactNode;
  /**
   * The rule sheet, rendered once over the whole day.
   *
   * It takes the two boxes it needs, because a modal over a day is two decisions and not one. The
   * sheet is a Base UI dialog, so it is portalled — by default to `<body>`, which would take it out
   * of the day entirely and anchor its `position: absolute` to the page.
   *
   * `container` is an EMPTY box holding nothing but the sheet, and `background` is everything the
   * sheet covers. Until 14 September 2026 both were the form's own element: the dialog was portalled
   * in beside the day's own controls, Base UI's focus guards did not bracket it, and Tab from the
   * foot walked into the day modal behind the scrim — where Enter on „Zamknij programowanie dnia"
   * closed the day and took the staged rule with it. The sheet makes `background` `inert` for as
   * long as it is out; the scrim is what the pointer meets there.
   */
  readonly renderSheet: (host: DaySheetHost) => ReactNode;
}

/** The two boxes the rule sheet needs: the empty one it lives in, and the one it covers. */
export interface DaySheetHost {
  readonly container: RefObject<HTMLElement | null>;
  readonly background: RefObject<HTMLElement | null>;
}

export interface DayFormProps {
  readonly daySlotToken: string;
  /**
   * The ABSOLUTE cycle this form is showing, or `null` for the authored base.
   *
   * `null` is the builder and is the default: the form renders exactly what it always has. Set is
   * the assigned-plan calendar's third host, opened on one session — every set row then shows that
   * week's numbers, and the one field the exercise progresses on can be held to that week alone.
   */
  readonly cycleIndex?: number | null;
  /**
   * Move the dock to another day within the phase, or `null` where there is no such move — the
   * calendar's editor is open on one session at one cycle. See `DayChromeProps` for why the arrows
   * are then absent rather than refused.
   */
  readonly onSelectDay: ((slotToken: string) => void) | null;
  readonly onClose: () => void;
  /**
   * Ask the host for the library — the two "+" affordances and the card head's `Rozgrzewka` chip.
   *
   * `targetExerciseSlotToken` names the exercise a warm-up is FOR, and it is what makes the chip's
   * pick a mini-group rather than another row in the day's warm-up run. `null` from the two pills,
   * which target a block and not a movement.
   *
   * **A host that draws a library owes this parameter a route to its rail** — the signature is where
   * that obligation is stated, rather than in a second boolean saying whether the host honoured it.
   * Ignore it, and the chip's pick appends to the day's warm-up run instead of binding to the lift.
   */
  readonly onRequestLibrary: (
    role: AuthorableExerciseRole,
    targetExerciseSlotToken: string | null,
  ) => void;
  /** The dock's always-mounted live region. Every structural act announces itself through it. */
  readonly announce: (message: string) => void;
  /** The id the dock's `aria-labelledby` points at, so the host is named by its own heading. */
  readonly headingId: string;
  /**
   * The plan's Save, so it is reachable from inside the dock. `null` when the plan cannot be
   * saved at all — a read-only document has nothing to offer rather than a control that refuses.
   */
  readonly save: DayFormSave | null;
  /**
   * The programmed days of the phase in view, worded — the "Przenieś do dnia…" targets, the two day
   * arrows' destinations, and what a drop on the week behind the panel resolves through.
   */
  readonly dayTargets: readonly DayMoveTarget[];
  readonly progression: DayProgression;
  /**
   * The exercise card owed focus — the one an add from the library just created, or `null`.
   *
   * The host decides whether an add moves focus at all: the builder's library is a DRAWER over the
   * day, so the press that worked also unmounts the row that was pressed and focus has to be sent
   * somewhere. A host with no library at all passes `null`.
   */
  readonly focusExerciseSlotToken?: string | null;
  /**
   * Draw this day in the host's other box — the panel's maximise, the modal's restore.
   *
   * `null` by default, which is every dock that has only one box: the calendar's day editor and the
   * client calendar's viewer. See `DayHostSwitch`.
   */
  readonly hostSwitch?: DayHostSwitch | null;
  /**
   * The day is being READ, not programmed — the client calendar's preview of a session that has not
   * happened yet (owner, 13 August 2026: *"wyszarzone komórki nieklikalne, bez biblioteki
   * ćwiczeń"*).
   *
   * It is a prop on this component rather than a second component because a viewer that drifted
   * from the editor would be worse than no viewer: the trainer is here to see what the client is
   * about to be given, and the only way that stays true is if the same code draws both. Every act
   * goes — the ⋯ menus, the type cycle, the two row buttons, "+ Parametr", the unit toggle,
   * "+ Dodaj serię", the drop zones and the grips — and every VALUE stays, in the same table, at
   * the same width. Fields become `readOnly` rather than `disabled`: a disabled input drops out of
   * the tab order, and the whole point of this surface is reading it.
   *
   * `false` is the default, and it is the builder and the calendar's day editor byte for byte.
   */
  readonly readOnly?: boolean;
}

export function DayForm({
  daySlotToken,
  cycleIndex = null,
  onSelectDay,
  onClose,
  onRequestLibrary,
  announce,
  headingId,
  save,
  dayTargets,
  progression,
  focusExerciseSlotToken = null,
  hostSwitch = null,
  readOnly = false,
}: DayFormProps) {
  const { view, previousDayToken, nextDayToken, t } = useDayProgramming(daySlotToken, cycleIndex, {
    // The library is a DRAWER over the day in every host that has one, so the two "+" pills say
    // "press" and nothing about dragging: the form behind a modal drawer is `inert` and a row
    // released over it lands on nothing. The one host that stood its library BESIDE the list left
    // on 13 September 2026 — see the header.
    libraryIsModal: true,
  });
  const move = useExerciseMove({ t, daySlotToken, dayTargets, announce });
  // Which cards a superset may still take, read off the same `supersetPick` each card draws its own
  // tick from — one answer, so the bar and the checkboxes cannot disagree. Warm-up run cards are
  // absent from `blocks` and are ineligible anyway. See `useDayPanelChrome`.
  const eligibleSupersetTokens = (view?.blocks ?? []).flatMap((block) =>
    (block.kind === 'single' ? [block.exercise] : block.members)
      .filter((exercise) => exercise.supersetPick.refusalReason === null)
      .map((exercise) => exercise.slotToken),
  );
  // The day's warm-up run, by token: those cards open FOLDED, as the mock draws them. Read off the
  // same view the run renders from, so a card cannot be drawn in the run and folded as main work.
  const warmupTokens = (view?.warmup.exercises ?? []).map((exercise) => exercise.slotToken);
  const chrome = useDayPanelChrome(daySlotToken, eligibleSupersetTokens, warmupTokens);
  // The list positions to draw: the day's own blocks, plus any the trainer just deleted, held where
  // they stood for the length of `cardOut`. The removal already reached the store — see
  // `model/departures`. Above the `view === null` guard, because a hook may not be conditional.
  const blockSlots = useDepartures(view?.blocks ?? [], CARD_OUT_MS, daySlotToken);
  const moveHintId = useId();
  const warmupFootnoteId = useId();
  const dayRestHintId = useId();
  /* The box the rule sheet covers, and therefore the containing block its absolute pane resolves
   * against. Handed to `renderSheet` rather than read by it: the sheet belongs to another feature
   * and may not reach into this one's DOM. */
  const formRef = useRef<HTMLDivElement>(null);
  /* Everything the rule sheet covers — the day's head and foot with it. It is a box of its own so
   * the sheet can make it `inert` while it is out; `.sheetHost` beside it holds the sheet alone. */
  const contentRef = useRef<HTMLDivElement>(null);
  const sheetHostRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  // The dock's own scroller: a long day clips, and a drag near its edge has to carry the list with
  // it or the trainer releases the pointer to scroll and has to start over.
  useAutoScroll(bodyRef, 'vertical');
  /* Stepping to another day: the cards arrive, one just after the next. Nothing on the panel's own
   * arrival — that is the panel's slide — and nothing at all where the viewer asked for stillness.
   * `lib/useDayStepMotion` carries the shape and why the body itself no longer fades. */
  useDayStepMotion(bodyRef, daySlotToken, useScreenMotionPreference());

  // The run-off under the last block. Without it the only way to land at the END of a day is the
  // bottom edge of the last item, which is a ~4px band — and a drop released just below it would
  // find no target at all and read as a cancel.
  /* The main-work list, which is where focus goes after a removal takes the card that held it away.
   * See `ExerciseMenu`'s `removalFocus`; the `<ol>` carries `tabIndex={-1}` for this and adds no tab
   * stop, exactly as the week card and the panel heading do for their own returns. */
  const listRef = useRef<HTMLOListElement>(null);
  const listEndRef = useRef<HTMLLIElement>(null);
  const listEnd = useDropTarget({
    ref: listEndRef,
    data: {
      kind: 'blockList',
      daySlotToken,
      blockCount: view?.blocks.length ?? 0,
    },
    canDrop: (source) =>
      !readOnly && (source.kind === 'exercise' || source.kind === 'libraryExercise'),
  });

  // The main work's dashed zone is the SAME append target. Its own copy tells the trainer to drag a
  // library row onto it — and until 3 Aug 2026 it was not a target at all, so the drop the zone
  // itself invited fell on nothing and read as a cancel. The run-off above it is a ~24px band; this
  // is the landing zone a pointer actually aims for, and on an empty day it is the only visible one.
  const addCardRef = useRef<HTMLButtonElement>(null);
  const addCardDrop = useDropTarget({
    ref: addCardRef,
    data: {
      kind: 'blockList',
      daySlotToken,
      blockCount: view?.blocks.length ?? 0,
    },
    canDrop: (source) =>
      !readOnly && (source.kind === 'exercise' || source.kind === 'libraryExercise'),
  });

  // The warm-up run's own zone, and the same reasoning one block over: since 10 August 2026 it says
  // "Przeciągnij ćwiczenie, by dodać rozgrzewkę", so it receives one.
  //
  // It takes a library row, and since 29 October 2026 (owner) a main-work block of this day too,
  // which becomes warm-up — the drag of the ⋯'s „Do rozgrzewki". The block is gated through the
  // same command the drop dispatches, so a stale drag reads as refused while it is still in the air.
  // Both APPEND: the run draws no positions to land between.
  const warmupRef = useRef<HTMLButtonElement>(null);
  const warmupDrop = useDropTarget({
    ref: warmupRef,
    data: { kind: 'warmupList', daySlotToken },
    canDrop: (source) => {
      if (readOnly) return false;
      if (source.kind === 'libraryExercise') return true;
      const exerciseSlotToken = source.exerciseSlotToken;
      return (
        source.kind === 'exercise' &&
        typeof exerciseSlotToken === 'string' &&
        previewMoveCommand({ kind: 'moveExerciseToWarmup', exerciseSlotToken }).kind !== 'refused'
      );
    },
  });

  /* ── A day whose token no longer resolves still has to be LEAVABLE ─────────────────────────
   * Two ways in: a link or a restored session naming a day that has been deleted, and a Cmd+Z that
   * removes the phase while the dock is open on one of its days.
   *
   * This branch drew an `h2` and a sentence until 13 September 2026 — no head at all, so no ✕. In
   * the panel the week behind it was still clickable, so the state was survivable by accident;
   * under the MODAL's scrim, with pointer dismissal off, a trainer with a mouse had the browser's
   * Back button and nothing else. The head is drawn, and the ✕ needs no `view`.
   *
   * The host toggle is NOT offered here, deliberately. Its press writes a sticky preference that
   * every following day obeys, and an error state is the one place a trainer is not choosing how to
   * work — a stray press on the way out would redecorate the next real day they open. The way out is
   * the act this state owes, and that is the ✕. */
  if (view === null) {
    return (
      <div className={styles.form}>
        <DayMissingHead headingId={headingId} title={t(`${KEY}.title`)} onClose={onClose} t={t} />
        <div className={styles.missing}>
          {/* A card with a drawing, like every other empty state in this app, rather than the bare
            * sentence in a dashed box this used to be. The calendar mark is the one that says "a
            * day", and the tone is `action` because the sentence ends in something to do. */}
          <EmptyState
            className={styles.missingCard}
            mark={<CalendarMarkIcon />}
            tone="action"
            title={t(`${KEY}.missing`)}
          />
        </div>
      </div>
    );
  }

  const dayLabel = (token: string | null) => {
    if (token === null) return null;
    const target = dayTargets.find((candidate) => candidate.slotToken === token);
    return target === undefined ? null : { token, label: target.label };
  };

  // The day's place among the phase's programmed days. Read off `dayTargets` rather than off the day
  // node, because which days a phase programmes and in what order is a fact about the PHASE, and the
  // host already hands that list over worded. A day the list does not carry — the calendar's editor,
  // open on one session — gets its weekday alone rather than a claim about a phase it cannot see.
  const dayPosition = dayTargets.findIndex((target) => target.slotToken === daySlotToken);
  // The ⋯'s day group, built once for the whole day: every card offers the same stops and the same
  // refusal, and asking per card would be one walk of `dayTargets` per exercise.
  const moveMenu = exerciseMoveMenu(t, daySlotToken, dayTargets);
  const superset = supersetModeView(t, chrome.superset.picks.length);

  const chromeProps = {
    view,
    headingId,
    save,
    hostSwitch,
    previousDay: dayLabel(previousDayToken),
    nextDay: dayLabel(nextDayToken),
    onSelectDay,
    onClose,
    t,
    readOnly,
    stepper: dayStepperWords(t, view.weekdayLabel),
    caption: dayPanelCaption(
      t,
      view.weekdayLabel,
      dayPosition === -1 ? null : dayPosition + 1,
      dayTargets.length,
    ),
    // A viewer folds and selects nothing: one mode leads to an act it does not offer, and the other
    // to a fold of a list the trainer came here to read in full.
    listModes: readOnly
      ? null
      : {
          collapseAllLabel: collapseAllLabel(t, chrome.fold.isCollapsedAll),
          isCollapsedAll: chrome.fold.isCollapsedAll,
          onToggleCollapseAll: chrome.fold.toggleAll,
          supersetLabel: superset.toggleLabel,
          isSupersetOn: chrome.superset.isOn,
          onToggleSuperset: chrome.superset.toggleMode,
        },
  };

  const renderCard = (
    exercise: ExerciseView,
    options: {
      readonly isGrouped: boolean;
      readonly drag: BlockDragSource | null;
      /** This lift's own warm-ups, drawn inside its card. See `PrepBoxView`. */
      readonly preps?: PrepBoxView | null;
      /** This card IS one of those warm-ups — the mock's mini card inside the box. */
      readonly isPrep?: boolean;
      /** A card on its way out — see `model/departures`. Read-only, and it claims no focus. */
      readonly isGhost?: boolean;
    },
  ) => (
    <ExerciseBlock
      key={exercise.key}
      exercise={exercise}
      dayRestSeconds={view.dayRest.seconds}
      isGrouped={options.isGrouped}
      drag={options.drag}
      /* The box's words come from the projection; the ramps' CARDS are built here, because each one
       * needs the fold state, the announcer and the read-only flag this level owns. A prep card
       * carries no lift of its own — the whole block moves by the `Grupa` strip above it — and no
       * prep box of its own, because a warm-up does not get a warm-up. */
      preps={
        options.preps == null
          ? null
          : {
              view: options.preps,
              cards: options.preps.members.map((member) =>
                renderCard(member, {
                  isGrouped: false,
                  drag: null,
                  isPrep: true,
                  isGhost: options.isGhost,
                }),
              ),
            }
      }
      isPrep={options.isPrep === true}
      progressionPanel={exercise.isWarmup ? null : progression.renderPanel(exercise.slotToken)}
      progressionMatrix={exercise.isWarmup ? null : progression.renderMatrix(exercise.slotToken)}
      isFolded={chrome.fold.isFolded(exercise.slotToken)}
      onToggleFold={() => {
        chrome.fold.toggle(exercise.slotToken);
      }}
      /* The tick exists only while the mode is on: an unticked checkbox on every card of every day
       * would be an affordance for an act nobody asked to start. */
      supersetPick={
        chrome.superset.isOn
          ? {
              isPicked: chrome.superset.isPicked(exercise.slotToken),
              onToggle: () => {
                chrome.superset.togglePick(exercise.slotToken);
              },
            }
          : null
      }
      /* The ⋯'s cross-day move — the menu twin of dropping a block on the week behind the panel,
       * dispatching the identical command through the same `moveBlock`. Withheld from a viewer and
       * from the day's standalone warm-up run, which holds no list position to move. Present on a
       * superset member too: the command addresses the BLOCK, so the menu moves exactly what the
       * lift on the frame would. */
      /* The head's `Rozgrzewka` chip — the library, aimed at this movement. Withheld from a viewer,
       * which has no library at all, and from a warm-up, which the view already says `null` for. */
      onAddWarmup={
        readOnly || exercise.isWarmup
          ? null
          : () => {
              onRequestLibrary('Warmup', exercise.slotToken);
            }
      }
      moveToDay={
        readOnly || exercise.isWarmup
          ? null
          : {
              view: moveMenu,
              onMove: (targetDaySlotToken, blockCount) => {
                move.moveBlock(
                  exercise.slotToken,
                  targetDaySlotToken,
                  blockCount,
                  exercise.name,
                );
              },
            }
      }
      removalFocus={listRef}
      t={t}
      announce={announce}
      readOnly={readOnly || options.isGhost === true}
      shouldTakeFocus={options.isGhost !== true && exercise.slotToken === focusExerciseSlotToken}
    />
  );

  return (
    <div ref={formRef} className={styles.form}>
      <span id={AI_WRITTEN_NOTE_ID} hidden>
        {t('web:aiPlan.proposal.writtenByBuddy')}
      </span>
      {/* Everything the sheet covers, in one box so the sheet can take it out of play while it is
        * out. See `DaySheetHost`. */}
      <div ref={contentRef} className={styles.content}>
        <DayPanelHead {...chromeProps} />

        {chrome.superset.isOn && (
          <DaySupersetBar
            mode={superset}
            onCancel={chrome.superset.clear}
            onJoin={() => {
              // Announced on the ANSWER, never on the press. `groupAsSuperset` reports what it
              // joined, and `null` means it changed nothing — a locked phase, or a selection that no
              // longer holds two eligible members. The mode stays open in that case, because closing
              // it would take the trainer's selection away without doing anything with it.
              const joined = groupAsSuperset(view.slotToken, chrome.superset.picks);
              if (joined === null) return;
              announce(supersetJoinedMessage(t, joined.memberSlotTokens.length));
              chrome.superset.clear();
            }}
          />
        )}

        {/* The stage: the list, the library beside it, and the rule sheet over the lower part of
          * both. `position: relative` is what the sheet's `top: 34%` is measured against — the
          * gallery anchors it to exactly this box and not to the modal. */}
        <div className={styles.stage}>
        {/* The scroller. `tabIndex` because a scrollable region has to be reachable by keyboard, and a
          * trainer who cannot reach it cannot read the bottom of a long day. */}
        {/* The list column, and the box the floating action is measured against. It exists so the
          * fade spans the LIST and not the library rail beside it — `.stage` holds both, and a dock
          * anchored to it washed out the rail's own footer. */}
        <div className={styles.listPane}>
        <div ref={bodyRef} className={styles.body} tabIndex={0}>
          {/* The programator draws no ⓘ anywhere in the panel, so the counting rule is the RUN's own
            * description rather than a control on its heading row. It is still announced on arrival,
            * to everyone, in every host — which is more than the popover managed, since it was
            * withheld from the read-only viewer along with every other press. */}
          <section
            className={styles.warmup}
            aria-label={view.warmup.title}
            aria-describedby={warmupFootnoteId}
          >
            <p id={warmupFootnoteId} className={srOnly.region}>
              {view.warmup.footnote}
            </p>
            <div className={styles.warmupHead}>
              <span className={styles.warmupTitle}>{view.warmup.title}</span>
              <span className={styles.warmupCounts}>{view.warmup.counts}</span>
              <span className={styles.spacer} />
            </div>

            {view.warmup.exercises.length > 0 && (
              <ul className={styles.warmupList}>
                {view.warmup.exercises.map((exercise, index) => (
                  <MoveableBlock
                    key={exercise.key}
                    daySlotToken={view.slotToken}
                    index={index}
                    blockCount={view.warmup.exercises.length}
                    leadSlotToken={exercise.slotToken}
                    name={exercise.name}
                    liftLabel={exerciseLiftLabel(t, exercise.name)}
                    isPendingTarget={false}
                    move={null}
                  >
                    {(drag) => renderCard(exercise, { isGrouped: false, drag })}
                  </MoveableBlock>
                ))}
              </ul>
            )}

            {/* Both dashed zones are ways INTO the library, and a viewer has no library. They are
              * removed rather than refused: a zone that says "drag an exercise here" and takes
              * nothing is worse than no zone. */}
            {readOnly ? null : (
            <div className={styles.warmupActions}>
              {/* The warm-up run's way in — the mock's white pill, centred under the run, and still
                * the run's drop target. It was a full-width dashed zone reading "Przeciągnij
                * ćwiczenie, by dodać rozgrzewkę" until 7 September 2026; see `DropZoneView` for why
                * the copy went back to a noun and what that did NOT take away. */}
              <button
                ref={warmupRef}
                type="button"
                className={`${String(styles.pill)} ${String(styles.pillWarmup)}`}
                data-target={warmupDrop.isOver ? '' : undefined}
                onClick={() => {
                  onRequestLibrary('Warmup', null);
                }}
              >
                <span className={styles.pillGlyph} aria-hidden="true">
                  +
                </span>
                {view.warmup.drop.label}
                <span className={srOnly.region}> {view.warmup.drop.hint}</span>
              </button>
            </div>
            )}
          </section>

          <div className={styles.blockHead}>
            <span className={styles.blockTitle}>{view.mainTitle}</span>
            <span className={styles.blockCounts}>{view.mainCounts}</span>
            <span className={styles.spacer} />
          </div>

          {/* "Edytujesz jeden tydzień …" — a sentence about what a SAVE will reach. In a viewer it
            * is not merely redundant, it is false: nothing here is being edited and there is no save
            * to scope. The host says what the surface is instead. */}
          {view.cycleNote !== null && !readOnly && (
            <p className={styles.cycleNote}>{view.cycleNote}</p>
          )}

          {/* ── A day with nothing in it is a CARD with a drawing, not a sentence ─────────────
            * Owner, 9 September 2026: "uładnij empty state dnia, jakaś duża karta z ikoną, tak samo
            * i przycisk + który tak samo triggeruje bibliotekę ćwiczeń". The mock draws no such card
            * — it never shows an empty day — so this is his decision and not the handoff's, recorded
            * here so a later fidelity pass does not read it as drift and delete it.
            *
            * It is `shared/ui/EmptyState` with the dumbbell the WEEK's own empty day already uses, so
            * the two empty days in this builder speak one language, and `tone="action"` because there
            * is something to do. The button is the floating pill's twin down to its classes and its
            * words: the same `onRequestLibrary('Main', null)`, the same `view.mainDrop` copy. A second
            * key for the same act would be two strings to keep in step.
            *
            * The pill itself stays, and stays the DROP target — a library row released on the fade at
            * the foot still appends to the day. This card takes no drop of its own: adding a second
            * target for one act would put two insertion points on a list that has none.
            *
            * It carries no hidden tail, and that is not a saving: the pill's tail is what makes the
            * two controls tell APART. Two buttons with one accessible name is a screen reader offering
            * the same choice twice with nothing to choose between, and Playwright's strict mode
            * reported it as such the moment this card landed. Here the sentence the tail would say is
            * already on screen, in the two lines directly above the button, so the name is the word
            * and the context is the card. */}
          {view.emptyDay !== null && (
            <div className={styles.emptyDay}>
              <EmptyState
                mark={<DumbbellMarkIcon />}
                tone="action"
                title={view.emptyDay.title}
                hint={view.emptyDay.hint}
              />
              {readOnly ? null : (
                <button
                  type="button"
                  className={`${String(styles.pill)} ${String(styles.pillMain)}`}
                  onClick={() => {
                    onRequestLibrary('Main', null);
                  }}
                >
                  <span className={styles.pillGlyph} aria-hidden="true">
                    +
                  </span>
                  {view.mainDrop.label}
                </button>
              )}
            </div>
          )}

          {/* The keyboard instruction, once for the whole dock — every block's row points its
            * `aria-describedby` here, so the sentence is announced when the ⋯'s "Przestaw" hands
            * focus to a row, and exists once rather than seven times. A viewer moves nothing. */}
          {readOnly ? null : (
            <p id={moveHintId} className={styles.moveHint}>
              {t('web:planBuilder.dnd.exerciseGripHint')}
            </p>
          )}

          <ol ref={listRef} className={styles.list} aria-label={view.mainTitle} tabIndex={-1}>
            {blockSlots.map((slot, index) => {
              const block = slot.item;
              const lead = block.kind === 'single' ? block.exercise : block.members[0];
              if (lead === undefined) return null;
              /* Already removed from the plan and still on screen for the length of `cardOut` — drawn
               * from the view it last had, `inert` so nothing in it is reachable, and read-only so
               * nothing inside it asks the store about a token that is gone. See `model/departures`,
               * and note that the REMOVAL is not deferred to play this. */
              if (slot.isDeparting) {
                return (
                  <li key={slot.key} className={styles.blockLeaving} aria-hidden="true" inert>
                    {block.kind === 'single' ? (
                      renderCard(block.exercise, {
                        isGrouped: false,
                        drag: null,
                        preps: block.preps,
                        isGhost: true,
                      })
                    ) : (
                      <div className={`${String(styles.bracket)} ${String(styles.bracketSuperset)}`}>
                        {block.members.map((member) =>
                          renderCard(member, { isGrouped: true, drag: null, isGhost: true }),
                        )}
                      </div>
                    )}
                  </li>
                );
              }
              return (
                <MoveableBlock
                  key={block.key}
                  daySlotToken={view.slotToken}
                  index={index}
                  blockCount={view.blocks.length}
                  leadSlotToken={lead.slotToken}
                  name={lead.name}
                  liftLabel={exerciseLiftLabel(t, lead.name)}
                  isPendingTarget={
                    move.pending !== null &&
                    move.pending.position === index &&
                    move.pending.blockToken !== lead.slotToken
                  }
                  /* No move at all in a viewer, rather than a refused one: there is no act behind it
                   * and a control that only explains itself is noise on every block of the day.
                   *
                   * None while the superset mode is on either, and for a related reason: the head of
                   * a card is then a checkbox target, and a press that sometimes ticks and sometimes
                   * lifts is one gesture doing two things. Selection is mutually exclusive with
                   * moving, and leaving it exclusive is what keeps both unambiguous. */
                  move={readOnly || chrome.superset.isOn ? null : {
                    hintId: moveHintId,
                    liftEntry: exerciseLiftEntry(t, view.blocks.length),
                    liftedWords: (position, total) =>
                      exerciseLiftedWords(t, lead.name, position, total),
                    positionWords: (position, total) => exercisePositionWords(t, position, total),
                    cancelledMessage: t('web:planBuilder.dnd.cancelled'),
                    announce,
                    moveBlock: move.moveBlock,
                    setPending: move.setPending,
                  }}
                >
                  {(drag) =>
                    block.kind === 'single' ? (
                      /* One card, and it carries its own warm-ups if it has any — the mock draws a
                       * mini-group as a lift with a railed box above its head rather than as a frame
                       * around two rows, so there is no second frame here any more. `.bracket` is the
                       * SUPERSET's alone. */
                      renderCard(block.exercise, { isGrouped: false, drag, preps: block.preps })
                    ) : (
                      /* The canon's accent ring, with its `SUPERSET` pill riding the top edge and its
                       * members stitched into one block. */
                      <div
                        className={`${String(styles.bracket)} ${String(styles.bracketSuperset)}`}
                        role="group"
                        aria-label={`${block.label}: ${block.note}`}
                      >
                        {/* The pill alone. The sentence beside it — "2a → 2b bez przerwy · przerwa
                          * po parze" — is drawn nowhere in the mock, so it lives in the frame's
                          * accessible name above and is announced instead of printed. */}
                        <div className={styles.bracketHead}>
                          <span className={styles.bracketTag}>{block.label}</span>
                        </div>
                        {block.members.map((member, memberIndex) =>
                          renderCard(member, {
                            isGrouped: true,
                            /* The frame is ONE list position, so it carries one LIFT — on its first
                             * member's ⋯, which is the row the eye reaches for. Both members'
                             * heads are drag SOURCES, because a trainer reaching for the pair aims
                             * at whichever of the two rows they were reading, and the mock marks
                             * every head `draggable`. Same block either way: the strip carries the
                             * lead's token. */
                            drag:
                              drag === null
                                ? null
                                : memberIndex === 0
                                  ? drag
                                  : { lift: null, strip: drag.strip },
                          }),
                        )}
                      </div>
                    )
                  }
                </MoveableBlock>
              );
            })}
            {/* The run-off under the last block — see `listEnd`. Presentational: it holds no content
              * and announcing an empty list item would be a row that is not there. */}
            <li
              ref={listEndRef}
              className={styles.listEnd}
              role="presentation"
              data-target={listEnd.isOver ? '' : undefined}
            />
          </ol>

          {/* The list's foot, as the 07.09 mock draws it: the day's rest on ONE row, and the session
            * note under it in a dashed field.
            *
            * The paragraph that used to explain what changing the seed restamps is behind the ⓘ — the
            * owner's standing rule, that an explanation lives under an ⓘ or it goes. Nothing about the
            * field changed; what went is three lines of prose on every day of every plan. */}
          <div className={styles.foot}>
            {/* The mock draws no "Domyślna przerwa między seriami" block, and the seed behind it is a
              * real value a trainer has to be able to change — so the row follows the SKIP-LINK
              * contract: out of the flow and out of sight, in the tab order, and painted only while
              * it holds focus. Nothing is drawn in the panel the mock draws, and no capability is
              * behind a gesture a keyboard cannot make. The ⓘ that explained what changing the seed
              * restamps is the field's own description now. */}
            <div className={styles.restRow}>
              <span className={styles.footLabel}>{view.dayRest.label}</span>
              <input
                className={styles.restField}
                type="text"
                inputMode="text"
                aria-label={view.dayRest.label}
                defaultValue={view.dayRest.text}
                readOnly={readOnly}
                // `key` on the committed value so the field re-mounts when an undo moves the day's
                // rest underneath it. An uncontrolled input otherwise keeps the abandoned text.
                key={view.dayRest.seconds}
                aria-describedby={readOnly ? undefined : dayRestHintId}
                onBlur={
                  readOnly
                    ? undefined
                    : (event) => {
                        const parsed = parseRestCell(event.target.value);
                        if (parsed !== null) setDayRest(view.slotToken, parsed);
                      }
                }
                onKeyDown={(event) => {
                  if (event.key === 'Escape') event.stopPropagation();
                }}
              />
              {/* A viewer has no seed to change, so it has nothing to be told about changing one. */}
              {readOnly ? null : (
                <span id={dayRestHintId} className={srOnly.region}>
                  {view.dayRest.hint}
                </span>
              )}
            </div>

            <div className={styles.noteBlock}>
              <span className={styles.footLabel}>{view.sessionNote.label}</span>
              <textarea
                className={styles.sessionNote}
                aria-label={view.sessionNote.label}
                maxLength={view.sessionNote.maxLength}
                defaultValue={view.sessionNote.value}
                readOnly={readOnly}
                // A viewer shows the note the client will read, and shows it EMPTY when there is none:
                // the placeholder is an invitation to write, and there is nothing to write with. Same
                // rule the exercise note already follows.
                placeholder={readOnly ? undefined : view.sessionNote.placeholder}
                onBlur={
                  readOnly
                    ? undefined
                    : (event) => {
                        setSessionNotes(view.slotToken, event.target.value);
                      }
                }
                onKeyDown={(event) => {
                  if (event.key === 'Escape') event.stopPropagation();
                }}
              />
            </div>
          </div>

          {/* The mock's 44px tail, so the floating dock's fade never covers the session note. */}
          <div className={styles.bodyEnd} aria-hidden="true" />
        </div>

          {/* The main work's way in, as the canon draws it: one floating action standing in a fade at
            * the foot of the panel rather than a dashed zone closing the list. It is still the LIST's
            * append target — a library row dropped on it lands at the end of the day — which is why
            * it keeps the drop ref it always had. The fade behind it takes no pointer, so the list
            * under it stays reachable. */}
          {readOnly ? null : (
            <div className={styles.addDock}>
              <button
                ref={addCardRef}
                type="button"
                className={`${String(styles.pill)} ${String(styles.pillMain)}`}
                data-target={addCardDrop.isOver ? '' : undefined}
                onClick={() => {
                  onRequestLibrary('Main', null);
                }}
              >
                <span className={styles.pillGlyph} aria-hidden="true">
                  +
                </span>
                {view.mainDrop.label}
                <span className={srOnly.region}> {view.mainDrop.hint}</span>
              </button>
            </div>
          )}
        </div>

        </div>

        {/* Drawn only where a host hands over a Save. Neither of the BUILDER's two hands one — both
          * save from the screen's top bar — so in practice this is the calendar day editor's bar.
          * See `DayPanelFoot`. */}
        <DayPanelFoot save={save} />
      </div>

      {/* The sheet's own box, and it holds NOTHING else: Base UI's focus guards bracket the
        * container's contents, so a dialog portalled in beside the day's own controls let Tab walk
        * straight out of it. `.form` carries the `position: relative` the pane's `position:
        * absolute` and the scrim's `inset: 0` both resolve against, which is what lets the sheet
        * cover the whole box — the day's head and foot with it, as the mock draws it. */}
      <div ref={sheetHostRef} className={styles.sheetHost} />
      {progression.renderSheet({ container: sheetHostRef, background: contentRef })}

      {/* What a cross-day move costs a client who is already on this plan, asked before it happens.
        * One dialog for the whole day rather than one per card — `useExerciseMove` holds the single
        * pending move, and all three paths into it are gated there. Focus lands on the main-work
        * list afterwards, because both answers can leave the control that raised this unmounted:
        * a confirm takes the card away, and the pointer path had no opener to begin with. */}
      <ConfirmDialog
        prompt={move.confirm}
        finalFocus={listRef}
        onConfirm={move.confirmMove}
        onCancel={move.cancelMove}
      />
    </div>
  );
}
